import debounce from 'lodash/debounce'
import projects_overlays from '../projects_overlays/projects_overlays'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'
import { isRevealRearmLocked, setElementBelow, showElementInView } from './reveal_state'
import {
	LOAD_MORE_WRAPPER_SELECTORS,
	clearDisabledInlineTransition,
	filterOutScrollSliderElements,
	getBelowStartBottom,
	getObserverOptions,
	getScopedRowBackgroundSelector,
	getScopedSelectorList,
	getScrollTransitionOffset,
	getTransitionDelayAlt,
	hasActiveNavigationTransition,
	isElementVisibleInViewport,
	isRevealAnimationDisabled,
	makeDelayNormalAgain,
	reduceDelay,
	shouldAnimateElement,
	shouldApplyTransitionDelay,
	showNavigationTransitionTargetIfVisibleAtRest,
	transitionWasDisabledInline,
} from './element_animate_in_on_scroll_helpers'
import { bindFullpageRevealHandlers, bindScrollSnapRevealHandlers } from './fullscreen_reveal_handlers'

// Union of project-overlay items + normal-page items. Used by the scroll /
// fp-slider handlers below that don't care which scope an element belongs to.
let items
// Per-scope item arrays. Lets a newpageshown for one scope unobserve its own
// previous items WITHOUT touching the other scope's still-active observations
// — important once newpageshown can fire twice per landing (project overlay
// content + lazy-loaded underlay) so the overlay's mid-animation observers
// don't get torn down when the underlay arrives.
let itemsProjectOverlay = []
let itemsNormalPage = []
let skipNextPositionCheck = false
let overlayIsClosing = false
let globalObserver = null

const parseFullscreenSliderIds = (value) => {
	if (Array.isArray(value)) {
		return value
	}
	if (typeof value !== 'string' || value === '') {
		return []
	}
	try {
		const parsed = JSON.parse(value)
		return Array.isArray(parsed) ? parsed : []
	} catch (error) {
		return []
	}
}

const isSqueezeModeActiveForContent = (type, obj, parent) => {
	const data = window.magneticSlidesPassedData
	if (!data || data.ms_slide_type !== 'css_squeeze') {
		return false
	}

	const breakpoint = Number.parseInt(data.breakpoint, 10)
	if (data.ms_disable_for_phone === 'on' && Number.isFinite(breakpoint) && window.innerWidth <= breakpoint) {
		return false
	}

	const root = document.querySelector(parent)
	if (!root) {
		return false
	}
	const phoneGrid = root.querySelector('#custom-phone-grid')
	const grid = window.innerWidth <= breakpoint && phoneGrid ? phoneGrid : root.querySelector('#grid')
	if (!grid || grid.querySelectorAll('.row').length <= 1) {
		return false
	}

	const contentType = type || (obj && obj.type) || document.body.dataset.type
	const rawId = obj && obj.id != null ? obj.id : document.body.dataset.id
	const id = Number.parseInt(rawId, 10)
	const configByType = {
		project: ['activeInProjects', 'individualProjectIds'],
		page: ['activeInPages', 'individualPageIds'],
		category: ['activeInCategories', 'individualCategoryIds'],
	}
	const config = configByType[contentType]
	if (!config) {
		return false
	}

	const activeSetting = data[config[0]]
	if (activeSetting === 'on' || activeSetting === 'all') {
		return true
	}
	if (activeSetting !== 'individual' || !Number.isFinite(id)) {
		return false
	}

	return parseFullscreenSliderIds(data[config[1]]).some((configuredId) => Number.parseInt(configuredId, 10) === id)
}

const disableRevealAnimationForSqueeze = (elements, previousItems, observer) => {
	previousItems.forEach((element) => observer.unobserve(element))
	elements.forEach((element) => {
		element.classList.add('no-animate-in-on-scroll')
		element.style.transitionDelay = ''
		showElementInView(element)
	})
}

const rearmElementBelowViewport = (item) => {
	if (item.classList.contains('below') || isRevealRearmLocked(item) || isRevealAnimationDisabled(item)) {
		return
	}
	const rect = item.getBoundingClientRect()
	if (rect.top > window.innerHeight) {
		setElementBelow(item)
	}
}

const revealBelowElementIfInViewport = (item) => {
	if (!doAnimation || !item.classList.contains('below') || isRevealAnimationDisabled(item)) {
		return
	}
	if (hasActiveNavigationTransition(item)) {
		return
	}
	if (!shouldAnimateElement(item)) {
		return
	}

	const rect = item.getBoundingClientRect()
	if (rect.width === 0 && rect.height === 0) {
		return
	}
	if (rect.bottom <= 0 || rect.top >= window.innerHeight + getScrollTransitionOffset()) {
		return
	}

	if (shouldApplyTransitionDelay()) {
		item.style.transitionDelay = getTransitionDelayAlt(item, rect) + 'ms'
	} else {
		item.style.transitionDelay = ''
	}
	showElementInView(item)
}

const getNormalPageRevealItems = () => {
	if (itemsNormalPage.length > 0) {
		return itemsNormalPage
	}

	const normalPageRowBackgroundSelector = [
		getScopedRowBackgroundSelector('body>.lay-content', '.in-view'),
		getScopedRowBackgroundSelector('body>.lay-content', '.below'),
	].filter(Boolean).join(',')
	const normalPageLoadMoreSelector = [
		getScopedSelectorList('body>.lay-content', LOAD_MORE_WRAPPER_SELECTORS, '.in-view'),
		getScopedSelectorList('body>.lay-content', LOAD_MORE_WRAPPER_SELECTORS, '.below'),
	].join(',')

	return Array.from(
		document.querySelectorAll(
			[
				'body>.lay-content .col.in-view, body>.lay-content .col.below',
				'body>.lay-content .thumb.in-view, body>.lay-content .thumb.below,' +
				'body>.lay-content .element-wrap.in-view, body>.lay-content .element-wrap.below,' +
				'body>.lay-content .lay-thumbnailgrid-filter-wrap.in-view, body>.lay-content .lay-thumbnailgrid-filter-wrap.below,' +
				'body>.lay-content .show-news.in-view, body>.lay-content .show-news.below,' +
				'body>.lay-content .hide-news.in-view, body>.lay-content .hide-news.below',
				normalPageLoadMoreSelector,
				normalPageRowBackgroundSelector
			].filter(Boolean).join(',')
		)
	).filter((element) => !isRevealAnimationDisabled(element))
}

const restoreNormalPageRevealStateAfterProjectOverlayClose = () => {
	getNormalPageRevealItems().forEach((element) => {
		if (!element || !element.classList || !element.classList.contains('in-view') || element.classList.contains('below')) {
			return
		}
		if (!isElementVisibleInViewport(element)) {
			return
		}

		const hasRevealLock = Number.parseFloat(element.dataset.layRevealLockedUntil || '0') > 0
		const inlineTransitionDisabled = transitionWasDisabledInline(element)

		if (element.style.opacity === '0' && (hasRevealLock || inlineTransitionDisabled)) {
			element.style.opacity = ''
		}
		if (inlineTransitionDisabled) {
			clearDisabledInlineTransition(element)
		}
		element.style.transitionDelay = ''
	})
}

const resyncNormalPageRevealAfterProjectOverlayClose = () => {
	resumeOnScrollAnimation()
	getNormalPageRevealItems().forEach((element) => {
		if (isRevealAnimationDisabled(element)) {
			return
		}
		if (!shouldAnimateElement(element)) {
			return
		}

		if (globalObserver) {
			globalObserver.observe(element)
		}

		if (!isElementVisibleInViewport(element)) {
			return
		}

		const rect = element.getBoundingClientRect()
		if (shouldApplyTransitionDelay()) {
			element.style.transitionDelay = getTransitionDelayAlt(element, rect) + 'ms'
		} else {
			element.style.transitionDelay = ''
		}
		showElementInView(element)
	})
	restoreNormalPageRevealStateAfterProjectOverlayClose()
}

var i = function () {
	if (window.layData.element_transition_on_scroll == 'on' && 'IntersectionObserver' in window) {
		// Track when overlay starts animating out (fired from animateOut function)
		window.laytheme.on('overlaystartsanimatingout', function () {
			overlayIsClosing = true
			pauseOnScrollAnimation()
		})

		const bindScrollSnapHandlers = function () {
			bindScrollSnapRevealHandlers({
				getItems: () => items,
				isOverlayClosing: () => overlayIsClosing,
			})
		}

		window.laytheme.on('newpageshown', function (layoutObj, type, obj, isProjectOverlayObj) {
			const isProjectOverlay = isProjectOverlayObj != undefined && isProjectOverlayObj.isProjectOverlay === true
			let parent = isProjectOverlay ? '#lay-project-overlay>.lay-content' : 'body>.lay-content'

			// Reuse one IntersectionObserver across both scopes. Previously we
			// disconnect()ed and recreated it on every newpageshown — fine in
			// single-fire mode but in the upcoming direct-load flow (overlay
			// fires first, underlay fires second) the second fire would tear
			// down the overlay's still-active observations mid-animation.
			if (globalObserver == null) {
				globalObserver = new IntersectionObserver(callback, getObserverOptions())
			}
			let observer = globalObserver
			const prevForScope = isProjectOverlay ? itemsProjectOverlay : itemsNormalPage

			//items: do not select any .col elements inside of .stack-has-border-or-shadow-or-rounded-corners
			// Also exclude grid-type containers (.type-elementgrid, .type-thumbnailgrid, .type-productsgrid, .type-news)
			// as they should never get .below/.in-view classes - only their inner content elements should
			const revealSelector = [
				parent + ' .col.no-parallax.no-offset.no-sticky:not(.stack-has-border-or-shadow-or-rounded-corners .col):not(.type-elementgrid):not(.type-thumbnailgrid):not(.type-productsgrid):not(.type-news):not(.inside-collapsed-row)',
				parent + ' .col.no-parallax.no-offset.type-thumbnailgrid .thumb',
				parent + ' .col.no-parallax.no-offset.type-elementgrid .element-wrap',
				parent + ' .col.no-parallax.no-offset.type-productsgrid li',
				parent + ' .lay-thumbnailgrid-filter-wrap',
				parent + ' .show-news',
				parent + ' .hide-news',
				getScopedSelectorList(parent, LOAD_MORE_WRAPPER_SELECTORS),
				getScopedRowBackgroundSelector(parent)
			].filter(Boolean).join(',')
			let newItems = filterOutScrollSliderElements(document.querySelectorAll(revealSelector))
			if (isSqueezeModeActiveForContent(type, obj, parent)) {
				disableRevealAnimationForSqueeze(newItems, prevForScope, observer)
				if (isProjectOverlay) {
					itemsProjectOverlay = []
				} else {
					itemsNormalPage = []
				}
				items = itemsProjectOverlay.concat(itemsNormalPage)
				return
			}
			// Exclude elements whose .col contains an inner `[data-lay-scroll-anim]`
			// wrapper. Those run the new per-element scroll-animation system
			// (Slide Up / Trigger & Play etc.) and would otherwise also get the
			// legacy `.below` → `.in-view` opacity fade-in here — visible as a
			// "double animation" on every Build-Animation target.
			newItems = Array.from(newItems).filter((n) => !isRevealAnimationDisabled(n) && !(n.matches && (n.matches('[data-lay-scroll-anim]') || n.querySelector('[data-lay-scroll-anim]'))))
			// Diagnostic: print which elements are picked up by the LEGACY
			// .below/.in-view animator AND already have a per-element scroll
			// animation. Both systems running on the same node animates the
			// element twice (one opacity fade from `.below`, plus the
			// data-attribute-driven scroll animation). Toggled by the
			// `LAY_ANIM_DEBUG` boolean in
			// `scroll_animations/scroll_animations.js` (mirrored onto
			// `window.__LAY_ANIM_DEBUG` at module load). */
			if (typeof window !== 'undefined' && window.__LAY_ANIM_DEBUG === true) {
				// The `data-lay-scroll-anim` attribute sits on an INNER wrap
				// inside the `.col`, so a same-node check misses it. Use a
				// descendant lookup (or self) to find true overlap.
				const overlap = Array.from(newItems).filter((n) => n.matches && (n.matches('[data-lay-scroll-anim]') || n.querySelector('[data-lay-scroll-anim]')))
				console.log('[lay-anim] legacy IO collected', newItems.length, 'item(s);', overlap.length, 'also contain data-lay-scroll-anim (will double-animate — legacy IO fades the .col while scroll-anim animates the inner wrap)')
				overlap.slice(0, 20).forEach((n) => {
					const img = n.querySelector('img')
					const fname = img ? img.src.split('/').pop() : ''
					console.log('[lay-anim]   ⚠ overlap', (n.className || '').toString().slice(0, 60), fname)
				})
			}
			// Diff-based observer sync. Elements that were in this scope's
			// previous set but aren't in `newItems` get unobserved.
			// Elements that are newly in `newItems` get observed below in
			// the runPositionCheckAndObserve callback. Elements that are
			// in BOTH (= unchanged across newpageshown) stay observed —
			// their IO state, .below / .in-view classes, and inferred
			// animation phase survive a same-scope re-fire untouched.
			const newItemsSet = new Set(newItems)
			prevForScope.forEach((el) => {
				if (!newItemsSet.has(el)) observer.unobserve(el)
			})
			const prevSet = new Set(prevForScope)
			const newlyAddedThisCycle = newItems.filter((el) => !prevSet.has(el))

			if (isProjectOverlay) {
				itemsProjectOverlay = newItems
			} else {
				itemsNormalPage = newItems
			}
			// `items` (union) is consumed by the scroll / fp-slider handlers
			// below — they don't care about scope, they just want every active
			// element so they can flip .below ⇄ .in-view at scroll bottom or on
			// fullpage slide change.
			items = itemsProjectOverlay.concat(itemsNormalPage)

			// add below classes to below elements
			// if i dont use a timeout this doesnt work well, especially when we have a masonry grid :)
			const runPositionCheckAndObserve = function () {
				let shouldRunPositionCheck = true
				// Skip position check if this page was loaded while overlay was closing
				// getBoundingClientRect() returns incorrect values in that scenario
				// (especially problematic with horizontal scroll snap where viewport is shifted)
				if (skipNextPositionCheck) {
					skipNextPositionCheck = false // Reset flag
					shouldRunPositionCheck = false
					// Don't add "below" classes - observers will handle it naturally once overlay is gone
				} else if (window.utility_controller.isProjectsOverlayEnabled() && overlayIsClosing) {
					// CRITICAL: Pause animations to prevent intersection observers from triggering during viewport shift
					pauseOnScrollAnimation()
					shouldRunPositionCheck = false
					// Don't add "below" classes yet - wait for destroyoverlay event
					// Just set up observers, they will handle it when overlay is gone
				} else {
					// Only walk newly-added items — items that were already in
					// the previous scope set kept their classes / observer state
					// across the newpageshown re-fire. Running the position-
					// check on them again would risk re-flipping `.below` mid-
					// animation if the previous handler hadn't completed yet.
					newlyAddedThisCycle.forEach((el) => {
						if (isRevealAnimationDisabled(el)) {
							return
						}

						// Skip while the navigation transition is animating this
						// element. anime.js applies a temporary translateY that
						// can push partially-visible elements below the fold —
						// without this guard they'd wrongly get .below + the
						// CSS opacity:0!important and miss the staggered fade-in.
						if (hasActiveNavigationTransition(el)) {
							return
						}

						// Check if element is hidden (display: none)
						var rect = el.getBoundingClientRect()
						var isHidden = rect.width === 0 && rect.height === 0

						// Give hidden elements the 'below' class so they animate when shown
						// Also give 'below' class to elements that are below the viewport
						if (isHidden || rect.top > getBelowStartBottom()) {
							el.classList.add('below')
						}
					})
				}
				// Observe only newly-added items. Elements that were already
				// being observed in the previous newpageshown keep their IO
				// subscription — no `unobserve` then `observe` dance, which
				// would otherwise create a brief gap where IO fires again
				// for already-positioned elements and double-classes them.
				const observeWhenTransitionSettled = function (el, attempt = 0) {
					if (hasActiveNavigationTransition(el) && (animation_lifecycle_controller.isNavigationAnimating() || attempt < 20)) {
						setTimeout(function () {
							observeWhenTransitionSettled(el, attempt + 1)
						}, 100)
						return
					}
					if (hasActiveNavigationTransition(el)) {
						observer.observe(el)
						return
					}

					if (shouldRunPositionCheck && !el.classList.contains('below') && !el.classList.contains('in-view') && !isRevealAnimationDisabled(el)) {
						if (showNavigationTransitionTargetIfVisibleAtRest(el)) {
							observer.observe(el)
							return
						}
						var rect = el.getBoundingClientRect()
						var isHidden = rect.width === 0 && rect.height === 0
						if (isHidden || rect.top > getBelowStartBottom()) {
							el.classList.add('below')
						}
					}

					observer.observe(el)
				}
				newlyAddedThisCycle.forEach((el) => {
					observeWhenTransitionSettled(el)
				})
			}
			setTimeout(function () {
				if (!animation_lifecycle_controller.isDocumentVisible()) {
					animation_lifecycle_controller.runWhenDocumentVisible(runPositionCheckAndObserve)
					return
				}
				runPositionCheckAndObserve()
			}, 500)
			bindScrollSnapHandlers()
		})

		// scrolled to bottom
		// if i wouldn't do this, small elements that are very close to the bottom would not ever show up
		window.eventsChannel.on('scroll', function (scrollTop, scrollHeight, scrollContext) {
			var itemsForScroll = items
			if (scrollContext && scrollContext.source === 'project-overlay') {
				itemsForScroll = itemsProjectOverlay
			} else if (scrollContext && scrollContext.source === 'normal-page') {
				itemsForScroll = itemsNormalPage
			}

			if (itemsForScroll) {
				itemsForScroll.forEach(revealBelowElementIfInViewport)
				itemsForScroll.forEach(rearmElementBelowViewport)
			}
			var visibleHeight = scrollContext && scrollContext.target && scrollContext.target.clientHeight
				? scrollContext.target.clientHeight
				: window.innerHeight
			if (visibleHeight + scrollTop >= scrollHeight - 200) {
				if (itemsForScroll) {
					for (let i = 0; i < itemsForScroll.length; i++) {
						const item = itemsForScroll[i]
						if (item.classList.contains('below')) {
							// Check if element should be animated (e.g., thumb with hidden parent should be skipped)
							if (!shouldAnimateElement(item)) {
								continue // Skip animation - parent is hidden
							}

							if (shouldApplyTransitionDelay()) {
								const transitionDelay = getTransitionDelayAlt(item, item.getBoundingClientRect())
								item.style.transitionDelay = transitionDelay + 'ms'
							} else {
								item.style.transitionDelay = ''
							}
							showElementInView(item)
						}
					}
				}
			}
		})

		bindFullpageRevealHandlers({
			getItemsProjectOverlay: () => itemsProjectOverlay,
			getItemsNormalPage: () => itemsNormalPage,
			isOverlayClosing: () => overlayIsClosing,
			isProjectOverlayOpen: () => projects_overlays.isOverlayDisplayBlock(),
		})

		bindResize()
		bindScrollSnapHandlers()
	}
}

var bindResize = function () {
	jQuery(window).off('window_width_changed', doResize)
	jQuery(window).on('window_width_changed', doResize)
}

var doResize = debounce(function () {
	if (items) {
		// setDistanceFromTopLeftCorner(Array.from(items))
	}
}, 2000)

window.laytheme.on('pushstate', function () {
	pauseOnScrollAnimation()
})
window.laytheme.on('popstate', function () {
	pauseOnScrollAnimation()
})
window.laytheme.on('newpageshown', function () {
	setTimeout(function () {
		// Don't resume animation if project overlay is closing
		// This prevents underlying elements from animating when the overlay closes
		if (window.utility_controller.isProjectsOverlayEnabled() && projects_overlays.shouldCloseOverlay()) {
			// Animation will be resumed by the destroyoverlay event
			return
		}
		resumeOnScrollAnimation()
	}, 50)
})

// Resume animations after overlay animation completes
window.laytheme.on('projectoverlayclosed', function () {
	setTimeout(function () {
		resyncNormalPageRevealAfterProjectOverlayClose()
	}, 50)
})

// Reset the overlayIsClosing flag after scroll snap is destroyed
// We delay this to ensure newpageshown's setTimeout(500) position check has completed
window.laytheme.on('destroyoverlay', function () {
	// Wait for newpageshown's setTimeout(500) to complete before resetting flag
	setTimeout(function () {
		overlayIsClosing = false
		resyncNormalPageRevealAfterProjectOverlayClose()
	}, 600)
})

var doAnimation = true
var resumeOnScrollAnimation = function () {
	doAnimation = true
}
var pauseOnScrollAnimation = function () {
	doAnimation = false
}

let callback = (entries, observer) => {
	entries.forEach((entry) => {
		// Skip while the navigation transition is animating this element —
		// the temporary translateY applied by anime.js makes IO report
		// non-intersecting for elements that are actually visible at rest,
		// which would wrongly toggle .below mid-animation.
		if (hasActiveNavigationTransition(entry.target)) {
			return
		}
		// Each entry describes an intersection change for one observed
		if (doAnimation) {
			if (entry.isIntersecting == true && entry.target.classList.contains('below') && (entry.boundingClientRect.top > 0 || entry.boundingClientRect.bottom > 0)) {
				// in view
				// const transitionDelay = getTransitionDelay(entry.target)
				if (!isRevealAnimationDisabled(entry.target)) {
					// Check if element should be animated (e.g., thumb with hidden parent should be skipped)
					if (!shouldAnimateElement(entry.target)) {
						return // Skip animation - parent is hidden
					}

					if (shouldApplyTransitionDelay()) {
						const transitionDelay = getTransitionDelayAlt(entry.target, entry.boundingClientRect)
						// addToArray(entry)
						// doOncePerSecond(entry)
						entry.target.style.transitionDelay = transitionDelay + 'ms'
					} else {
						entry.target.style.transitionDelay = ''
					}

					showElementInView(entry.target)
					// Dev-mode tracking: one event per element-animate-in
					// firing. The dev-mode overlay attributes scope from
					// the DOM ancestry so "doubled animations" in an
					// overlay are immediately visible in the log.
					if (typeof window.layDevModeEmit === 'function') {
						window.layDevModeEmit('element_animate_in:fired', entry.target)
					}
				}
			}

			if (entry.isIntersecting == false && entry.boundingClientRect.top > window.innerHeight && !isRevealRearmLocked(entry.target) && !isRevealAnimationDisabled(entry.target)) {
				// below view
				setElementBelow(entry.target)
			}
		}
	})
}

var element_animate_in_on_scroll_controller = {
	i: i,
	reduceDelay: reduceDelay,
	makeDelayNormalAgain: makeDelayNormalAgain,
	pauseOnScrollAnimation: pauseOnScrollAnimation,
	resumeOnScrollAnimation: resumeOnScrollAnimation,
}

export default element_animate_in_on_scroll_controller
